import type { HttpCookie, HttpHeaders, ResponseCookie } from '../../types/web/http';
import { type AddressInfo, isIP } from 'node:net';
import { Cookie } from 'tough-cookie';

function parseHost(headers: HttpHeaders, defaultHost?: string): string | undefined {
    let host = headers.get('x-forwarded-host');
    if (Array.isArray(host)) {
        host = host[0];
    }
    if (host) {
        const port = headers.one('x-forwarded-port');
        if (port) {
            host = `${host}:${port}`;
        }
    }
    host ??= headers.one('host');
    if (Array.isArray(host)) {
        host = host[0];
    }
    if (host) {
        return (host as string).split(',', 1)[0].trim();
    }

    return defaultHost;
}

function isForwardedSsl(headers: HttpHeaders): boolean {
    const v = headers.one('x-forwarded-ssl');
    return (typeof v === 'string') && (v.toLowerCase() === 'on');
}

function parseProtocol(headers: HttpHeaders, defaultProtocol: string): string {
    let proto = headers.get('x-forwarded-proto');

    if (Array.isArray(proto)) {
        proto = proto[0];
    }
    if (proto !== undefined) {
        return (proto as string).split(',', 1)[0].trim();
    }
    else if (isForwardedSsl(headers)) {
        return 'https';
    }
    return defaultProtocol;
}

function parseRemoteAddress(url: URL, headers: HttpHeaders, remoteAddress?: AddressInfo) : AddressInfo | undefined {
    const port = remoteAddress ? remoteAddress.port : 'https:' === url.protocol ? 443 : 80;

    let host = headers.one('x-forwarded-for');
    if (Array.isArray(host)) {
        host = host[0];
    }
    if (host !== undefined) {
        host = (host as string).split(',', 1)[0].trim();
        return { address: host, port: Number(port), family: isIP(host) === 6 ? 'IPv6' : 'IPv4' };
    }
}

export abstract class AbstractHttpMessage<Headers extends HttpHeaders = HttpHeaders> {
    readonly #headers: Headers;
    protected constructor(headers: Headers) {
        this.#headers = headers;
    }
    get headers(): Headers {
        return this.#headers;
    }
}

export abstract class AbstractHttpRequest<Headers extends HttpHeaders = HttpHeaders> extends AbstractHttpMessage<Headers> {
    private static logIdCounter = 0;
    #id?: string

    get id(): string {
        if (this.#id === undefined) {
            this.#id = `${this.initId()}-${++AbstractHttpRequest.logIdCounter}`;

        }
        return this.#id;
    }

    protected initId(): string {
        return 'request';
    }

    get cookies(): HttpCookie[] {
        return parseCookies(this.headers);
    }

    protected parseHost(defaultHost?: string): string | undefined {
        return parseHost(this.headers, defaultHost);
    }

    protected parseProtocol(defaultProtocol: string): string {
        return parseProtocol(this.headers, defaultProtocol);
    }

    abstract get URL(): URL;

    protected parseRemoteAddress(remoteAddress?: AddressInfo) {
        return parseRemoteAddress(this.URL, this.headers, remoteAddress);
    }
}

export abstract class AbstractHttpResponse<Headers extends HttpHeaders = HttpHeaders> extends AbstractHttpMessage<Headers> {
    get cookies(): ResponseCookie[] {
        return parseResponseCookies(this.headers);
    }

    protected setCookieValue(responseCookie: ResponseCookie): string {
        const cookie = new Cookie({
            key: responseCookie.name,
            value: responseCookie.value,
            maxAge: responseCookie.maxAge,
            domain: responseCookie.domain,
            path: responseCookie.path,
            secure: responseCookie.secure,
            httpOnly: responseCookie.httpOnly,
            sameSite: responseCookie.sameSite
        });
        return cookie.toString();
    }

}

function parseCookies(headers: HttpHeaders): HttpCookie[] {
    return headers.list('cookie')
        .map(s => s.split(';').map((cs) => Cookie.parse(cs)))
        .flat(1)
        .filter((tc) => tc !== undefined)
        .map((tc) => {
            const result: HttpCookie = Object.freeze({name: tc.key, value: tc.value});
            return result;
        });
}

type Mutable<T> = {-readonly [K in keyof T]: T[K]};

function parseResponseCookies(headers: HttpHeaders): ResponseCookie[] {
    return headers.list('set-cookie').map((cookie) => {
        const parsed = Cookie.parse(cookie);
        if (parsed) {
            const result: Mutable<ResponseCookie> = {name: parsed.key, value: parsed.value, maxAge: Number(parsed.maxAge ?? -1)};
            if (parsed.httpOnly) result.httpOnly = true;
            if (parsed.domain) result.domain = parsed.domain;
            if (parsed.path) result.path = parsed.path;
            if (parsed.secure) result.secure = true;
            if (parsed.httpOnly) result.httpOnly = true;
            if (parsed.sameSite) result.sameSite = parsed.sameSite as 'strict' | 'lax' | 'none';
            return Object.freeze(result);
        }
    }).filter((cookie): cookie is ResponseCookie => cookie !== undefined);
}
